import { useEffect, useState } from 'react';
import type { ConfigurationFormMethods } from 'src/pages/ConfigurationPage/hooks/useConfigurationForm';
import type { Workflow } from 'src/pages/ConfigurationPage/WorkflowsTab/workflowFormValues';
import type { PersonaInputData } from 'src/pages/ConfigurationPage/utils/getPersonaFormValues';
import type { ConfigurationFormFieldsObject } from 'src/pages/ConfigurationPage/hooks/useConfigurationFormFields';

export type WorkflowListKey = 'pf_default_workflow' | 'pf_custom_workflows';

// Owns the default/custom workflow selections, keeping them in sync with
// both the active version's template defaults and its persisted input data,
// and mirrors edits into the persona form's values.
export const useConfigurationWorkflows = ({
  formFieldsObject,
  inputData,
  methods,
}: {
  formFieldsObject: ConfigurationFormFieldsObject;
  inputData: PersonaInputData | null;
  methods: ConfigurationFormMethods;
}) => {
  const [defaultWorkflows, setDefaultWorkflows] = useState<Workflow[]>(
    inputData?.pf_default_workflow ? [inputData.pf_default_workflow] : [],
  );
  const [customWorkflows, setCustomWorkflows] = useState<Workflow[]>(
    inputData?.pf_custom_workflows || [],
  );

  useEffect(() => {
    setDefaultWorkflows(
      formFieldsObject?.pf_default_workflow
        ? [formFieldsObject.pf_default_workflow.default_value.value]
        : [],
    );
  }, [formFieldsObject]);

  useEffect(() => {
    if (inputData) {
      setDefaultWorkflows(inputData?.pf_default_workflow ? [inputData.pf_default_workflow] : []);
      setCustomWorkflows(inputData?.pf_custom_workflows || []);
    }
  }, [inputData]);

  const onWorkflowsChange = (key: WorkflowListKey, data: Workflow[]) => {
    if (key === 'pf_default_workflow') {
      setDefaultWorkflows(data);
      methods.setValue('pf_default_workflow', data[0]);
    }
    if (key === 'pf_custom_workflows') {
      setCustomWorkflows(data);
      methods.setValue('pf_custom_workflows', data);
    }
  };

  const onWorkflowAdd = (workflow: Workflow) => {
    const newCustomWorkflows = [...customWorkflows, workflow];

    setCustomWorkflows(newCustomWorkflows);
    methods.setValue('pf_custom_workflows', newCustomWorkflows);
  };

  return {
    defaultWorkflows,
    customWorkflows,
    setDefaultWorkflows,
    setCustomWorkflows,
    onWorkflowsChange,
    onWorkflowAdd,
  };
};
